import React, { useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import "./index.scss"

import { ConfigContainer } from "components"
import ToggleButton from "react-bootstrap/ToggleButton"
import ToggleButtonGroup from "react-bootstrap/ToggleButtonGroup"
import { Duration } from "luxon"
import { Title, Container, Text, DurationInput, Select } from "components/Form"
import { members, message, analyses, seatInfo, customerAdministration, auth } from "state_management"
import useT from "utils/hooks/useT"

const PLANNERTECH_LANGUAGE_CODES = ["en", "da", "fr", "it", "es", "pl", "de", "el", "pt", "cs"]
const PLANNERTECH_LANGUAGE_OPTIONS = [
  { id: "cs", name: "Čeština" },
  { id: "da", name: "Dansk" },
  { id: "de", name: "Deutsch" },
  { id: "el", name: "Ελληνικά" },
  { id: "en", name: "English" },
  { id: "es", name: "Español" },
  { id: "fr", name: "Français" },
  { id: "it", name: "Italiano" },
  { id: "pl", name: "Polski" },
  { id: "pt", name: "Português" },
]

// Member config.
// Used both by ADMINs, MEMBERs and PLANNERs, so make sure it keeps working
// regardless of which membertype the currently logged in user is!
export default function MemberConfig({ member = null, adminTeamId = null, onClose }) {
  const authState = useSelector((state) => state.auth)
  const dispatch = useDispatch()
  const { t } = useT("common")

  const [role, setRole] = useState(member ? member.role : "MEMBER")
  const [startTime, setStartTime] = useState(
    member ? Duration.fromObject({ seconds: member.workHours.min }) : Duration.fromObject({ hours: 8 })
  )
  const [endTime, setEndTime] = useState(
    member ? Duration.fromObject({ seconds: member.workHours.max }) : Duration.fromObject({ hours: 16 })
  )
  const isCustomerAdmin = useSelector((state) => state.auth.role) === "customer_admin"
  const onlyLibrarian = useSelector((state) => state.teamStatus.onlyLibrarian)

  const [username, setUsername] = useState(member ? (member.name ? member.name : member.username)  : "")  // temporary check if DB changes name to username
  const [email, setEmail] = useState(member ? member.email : "")
  const [password, setPassword] = useState(null)
  const [initials, setInitials] = useState(member ? member.initials : "")
  const [showOnPlan, setShowOnPlan] = useState(member ? member.showOnPlan : true)
  const [outlookLink, setOutlookLink] = useState(member ? member.outlookLink : null)
  const [plannertechLanguage, setPlannertechLanguage] = useState(() => {
    if (!member) {
      return ""
    }
    return PLANNERTECH_LANGUAGE_CODES.includes(member.plannertechLanguage) ? member.plannertechLanguage : "en"
  })


  const days = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"]

  const renderDays = (disabled) => {
    return days.map((day) => {
      return (
        <ToggleButton
          disabled={disabled}
          className="Memberconfig-checkboxButton"
          value={day}
          title={day}
          variant="light"
          key={day}
          id={"member-config-"+day}
        >
          {day.substring(0, 3)}
        </ToggleButton>
      )
    })
  }

  const [workWeek, setWorkWeek] = useState(
    (() => {
      if (member) {
        let arr = []
        Object.entries(member.workWeek).map((entry) => {
          if (entry[1]) {
            arr.push(entry[0])
          }
          return null
        })
        return arr
      } else {
        return [days[0], days[1], days[2], days[3], days[4]]
      }
    })()
  )

  const checkOutlookLink = (link) => {
    let correctLink = true
    if(link && !link.includes('.ics')){
      dispatch(message.warning(t("mustBeIcsLink")))
      correctLink = false
    }
    if(link && link.length === 0){
      correctLink = true
    }
    setOutlookLink(link)
    return correctLink
  }

  const isPasswordValid = (password) => {
    // Define all password requirements and their respective regex patterns
    const requirements = [
      { name: t("atLeast8Characters"), pattern: /^.{8,}$/ },
      { name: t("atLeast1LowercaseLetter"), pattern: /[a-z]/ },
      { name: t("atLeast1UppercaseLetter"), pattern: /[A-Z]/ },
      { name: t("atLeast1Number"), pattern: /\d/ },
    ]

    if (member && !password) {
      return { valid: true, missingRequirements: [] }
    }

    let missingRequirements = []

    // Check each requirement
    for (let requirement of requirements) {
      if (!requirement.pattern.test(password)) {
        missingRequirements.push(requirement.name)
      }
    }

    // Return validity and list of missing requirements
    return {
      valid: missingRequirements.length === 0,
      missingRequirements: missingRequirements,
    }
  }

  const disabled = member && ((authState.isMember && member && authState.memberId !== member.id) || member.archived)
  return (
    <ConfigContainer
      disabled={disabled}
      title={member ? t("editMember") : t("addMember")}
      submitHandler={() => {
        const passwordResult = isPasswordValid(password)
        if(!passwordResult.valid) {
          const passwordMessage = t("missingPasswordRequirements", { requirements: passwordResult.missingRequirements.join(', ') })
          return dispatch(message.warning(passwordMessage))
        }
        const normalizedPlannertechLanguage = PLANNERTECH_LANGUAGE_CODES.includes(plannertechLanguage)
          ? plannertechLanguage
          : ""
        const currentPlannertechLanguage = member
          ? (PLANNERTECH_LANGUAGE_CODES.includes(member.plannertechLanguage) ? member.plannertechLanguage : "en")
          : ""
        const res = {
          name: username,
          initials,
          email,
          workHours: {
            min: startTime.as("seconds"),
            max: endTime.as("seconds"),
          },
          workWeek: (() => {
            let obj = {}
            days.map((day) => {
              obj[day] = false
              return null
            })
            workWeek.map((day) => {
              obj[day] = true
              return null
            })
            return obj
          })(),
          role,
          password: password || undefined, // Don't send password if it's not set!
          showOnPlan: role === "VIEW_ONLY" ? false : showOnPlan,
          outlookLink: outlookLink || null,
        }
        if (!member && normalizedPlannertechLanguage) {
          res.plannertechLanguage = normalizedPlannertechLanguage
        }
        if (member && normalizedPlannertechLanguage && normalizedPlannertechLanguage !== currentPlannertechLanguage) {
          res.plannertechLanguage = normalizedPlannertechLanguage
        }
        // Only use this if the user is exists
        if (!checkOutlookLink(outlookLink)){
          dispatch(message.warning(t("invalidOutlookLink")))
        }
        else if (member) {
          const wasShownOnPlan = member.showOnPlan
          dispatch(members.edit(member.id, res)).then(() => {
            if (wasShownOnPlan && !res.showOnPlan){
              dispatch(analyses.fetchPlanned())
              dispatch(analyses.fetchUnplanned())
            }
            if (member.id === authState.memberId && res.plannertechLanguage) {
              dispatch(auth.setPlannertechLanguage(res.plannertechLanguage))
            }
          }).then(onClose)
        } else {
          if (adminTeamId) {
            dispatch(members.createAsAdmin({ ...res, teamId: adminTeamId })).then(() => {
              // Fetching customers in different ways, depending on if user is customer-admin or admin for a company
              if(isCustomerAdmin) {
                dispatch(customerAdministration.fetchCustomers())
              } else {
                dispatch(seatInfo.fetch())
              }
            }).then(onClose)
          } else {
            dispatch(members.create(res)).then(onClose)
          }
        }
      }}
      cancelHandler={onClose}
    >
      <Container>
        <Title>{t("username")}</Title>
        <Text value={username} disabled={disabled} required onChange={setUsername} />
      </Container>
      <Container>
        <Title>{t("initials")}</Title>
        <Text required disabled={disabled} value={initials} textTransform={"uppercase"} onChange={(value) => setInitials(value)} />
      </Container>
      <Container>
        <Title>{t("emailLabel")}</Title>
        <Text
          required
          type="email"
          disabled={disabled}
          value={email}
          onValidate={(value) => {
            if (value.includes("..")) {
              return t("emailCannotContainDoubleDots")
            }
          }}
          textTransform={"lowercase"}
          onChange={(value) => setEmail(value)}
        />
      </Container>
      <Container>
        <Title>{t("plannertechLanguage")}</Title>
        <Select
          isMulti={false}
          required={!!member}
          disabled={disabled}
          collection={PLANNERTECH_LANGUAGE_OPTIONS}
          value={plannertechLanguage}
          onChange={(values) => setPlannertechLanguage(values[0] || "")}
          includeNone={!member}
          noneLabel={t("default")}
        />
      </Container>
      <Container>
        <Title>{t("password")}</Title>
        <Text type="password" disabled={disabled} required={!member} value={password} onChange={setPassword} />
      </Container>
      <Container>
        <Title>{t("role")}</Title>
        <ToggleButtonGroup
          type="radio"
          className="Memberconfig-toggleButtonGroup"
          name="role"
          defaultValue={member ? member.role : "MEMBER"}
          onChange={setRole}
        >
          <ToggleButton
            disabled={disabled || authState.isMember || authState.isViewOnly}
            value="MEMBER"
            variant="light"
            id="memberconfig-ismember_id"
            className="Memberconfig-radioButton"
            >
            {t("member")}
          </ToggleButton>
          <ToggleButton
            disabled={disabled || authState.isMember || authState.isViewOnly}
            value="PLANNER"
            variant="light"
            id="memberconfig-isplanner_id"
            className="Memberconfig-radioButton"
          >
            {t("planner")}
          </ToggleButton>
          <ToggleButton
            disabled={disabled || authState.isMember || authState.isViewOnly}
            value="VIEW_ONLY"
            variant="light"
            id="memberconfig-isviewonly_id"
            className="Memberconfig-radioButton"
          >
            {t("viewOnly")}
          </ToggleButton>
        </ToggleButtonGroup>
      </Container>
      {!onlyLibrarian && (
      <div>
      <Container>
        <Title>{t("showOnPlan")}</Title>
        <ToggleButtonGroup
          name="showOnPlan"
          className="Memberconfig-toggleButtonGroup"
          type="radio"
          value={role === "VIEW_ONLY" ? false : showOnPlan}
          onChange={setShowOnPlan}
        >
          <ToggleButton disabled={disabled || role === "VIEW_ONLY"} value={true} variant="light" id="memberconfig-showonplan_id" className="Memberconfig-radioButton">
            {t("shown")}
          </ToggleButton>
          <ToggleButton disabled={disabled || role === "VIEW_ONLY"} value={false} variant="light" id="memberconfig-hideonplan_id" className="Memberconfig-radioButton">
            {t("hidden")}
          </ToggleButton>
        </ToggleButtonGroup>
      </Container>
      <Container>
        <Title>{t("outlookLinkLabel")}</Title>
        <Text type="url" disabled={disabled} value={outlookLink} onChange={checkOutlookLink} />
      </Container>
      <div className="MemberConfig-availability">
        <p>{t("availability")}</p>
        <label>{t("workHours")}</label>
        <Container>
          <Title>{t("from")}</Title>
          <DurationInput disabled={disabled} value={startTime} onChange={setStartTime} required isTime />
          <Title>{t("to")}</Title>
          <DurationInput disabled={disabled} value={endTime} onChange={setEndTime} required isTime />
        </Container>
        <label>{t("workWeek")}</label>
        <div className="MemberConfig-work-week">
          <Container>
            <ToggleButtonGroup
              variant="light"
              type="checkbox"
              value={workWeek}
              onChange={(e) => {
                setWorkWeek(e)
              }}
            >
              {renderDays(disabled)}
            </ToggleButtonGroup>
          </Container>
        </div>
      </div>
      </div>
      )}
    </ConfigContainer>
  )
}
